import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex items-center justify-between gap-2 bg-amber-500 px-4 py-1.5 text-sm font-medium text-amber-950"
}
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "flex h-16 shrink-0 items-center gap-2 border-b px-4" }
const _hoisted_5 = { class: "ml-auto flex items-center gap-2" }
const _hoisted_6 = { class: "hidden md:inline max-w-[10rem] truncate" }
const _hoisted_7 = { class: "truncate" }
const _hoisted_8 = { class: "sr-only" }
const _hoisted_9 = { class: "sr-only" }
const _hoisted_10 = { class: "flex flex-col space-y-1" }
const _hoisted_11 = { class: "text-sm font-medium leading-none" }
const _hoisted_12 = { class: "text-xs leading-none text-muted-foreground" }
const _hoisted_13 = {
  key: 0,
  class: "text-xs leading-none text-muted-foreground"
}

import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useNotificationsStore } from '@/stores/notifications'
import { getInitials } from '@/utils/formatters'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import ThemeToggle from './ThemeToggle.vue'
import NotificationsDrawer from './NotificationsDrawer.vue'
import MeetingsWidget from './MeetingsWidget.vue'
import GlobalSearch from './GlobalSearch.vue'
import { useTasksWidget } from '@/composables/useTasksWidget'
import { Bell, User, Settings, LogOut, Globe, CalendarDays, X, CheckSquare, Building2, Check } from 'lucide-vue-next'
import type { Tenant } from '@/types'


export default /*@__PURE__*/_defineComponent({
  __name: 'Navbar',
  setup(__props) {

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const {
  user, logout, updatePreferences, isImpersonating, currentTenant, stopImpersonation,
  canSwitchTenant, fetchSelectableTenants, selectTenant, effectiveTenantId, isSuperAdmin,
} = useAuth()
const notificationsStore = useNotificationsStore()
const tasksWidget = useTasksWidget()

const isNotificationsOpen = ref(false)
const isMeetingsOpen = ref(false)

// Multi-tenant (Modelo C): tenants entre los que el usuario puede cambiar.
const selectableTenants = ref<Tenant[]>([])
const loadSelectableTenants = async () => {
  selectableTenants.value = canSwitchTenant.value ? await fetchSelectableTenants() : []
}
onMounted(loadSelectableTenants)
watch(() => user.value?.id, loadSelectableTenants)

const handleSelectTenant = async (tenantId: string) => {
  if (tenantId !== effectiveTenantId.value) await selectTenant(tenantId)
}

const breadcrumbs = computed(() => {
  const crumbs: Array<{ title: string; path?: string }> = [
    { title: t('navigation.dashboard'), path: '/dashboard' },
  ]

  const pathParts = route.path.split('/').filter(Boolean)

  if (pathParts.length > 1) {
    const section = pathParts[0]
    const sectionTitle = t(`navigation.${section}`, section)
    crumbs.push({ title: sectionTitle, path: `/${section}` })
  }

  if (route.meta?.title) {
    crumbs.push({ title: route.meta.title as string })
  }

  return crumbs
})

const toggleLanguage = async () => {
  const newLocale = locale.value === 'es' ? 'en' : 'es'
  locale.value = newLocale
  localStorage.setItem('locale', newLocale)

  if (user.value) {
    try {
      await updatePreferences({ language: newLocale })
    } catch {
      // Silently fail
    }
  }
}

const handleLogout = async () => {
  await logout()
}

const goToProfile = () => {
  router.push('/profile')
}

const goToSettings = () => {
  router.push('/settings')
}

const openNotifications = () => {
  isNotificationsOpen.value = true
}

const openMeetings = () => {
  isMeetingsOpen.value = true
}

const handleStopImpersonation = async () => {
  if (isSuperAdmin.value) {
    // Superadmin: salir de la impersonación y volver al panel de tenants.
    await stopImpersonation()
    router.push('/super-admin/tenants')
  } else {
    // Multi-tenant: volver al tenant PRIMARIO (selectTenant persiste + recarga).
    if (user.value?.tenantId) await selectTenant(user.value.tenantId)
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(isImpersonating) && _unref(currentTenant)?.name)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (_unref(isSuperAdmin))
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
                _cache[3] || (_cache[3] = _createTextVNode("Impersonando tenant: ", -1)),
                _createElementVNode("strong", null, _toDisplayString(_unref(currentTenant).name), 1)
              ]))
            : (_openBlock(), _createElementBlock("span", _hoisted_3, [
                _cache[4] || (_cache[4] = _createTextVNode("Trabajando en: ", -1)),
                _createElementVNode("strong", null, _toDisplayString(_unref(currentTenant).name), 1)
              ])),
          _createVNode(_unref(Button), {
            variant: "ghost",
            size: "sm",
            class: "h-6 text-amber-950 hover:bg-amber-600 hover:text-amber-950",
            onClick: handleStopImpersonation
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(X), { class: "mr-1 h-3 w-3" }),
              _createTextVNode(" " + _toDisplayString(_unref(isSuperAdmin) ? 'Salir' : 'Volver a mi tenant'), 1)
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("header", _hoisted_4, [
      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "w-9 md:hidden" }, null, -1)),
      _createVNode(_unref(Breadcrumb), { class: "hidden md:flex" }, {
        default: _withCtx(() => [
          _createVNode(_unref(BreadcrumbList), null, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(breadcrumbs.value, (crumb, index) => {
                return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                  _createVNode(_unref(BreadcrumbItem), null, {
                    default: _withCtx(() => [
                      (crumb.path)
                        ? (_openBlock(), _createBlock(_unref(BreadcrumbLink), {
                            key: 0,
                            href: crumb.path
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(crumb.title), 1)
                            ]),
                            _: 2
                          }, 1032, ["href"]))
                        : (_openBlock(), _createBlock(_unref(BreadcrumbPage), { key: 1 }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(crumb.title), 1)
                            ]),
                            _: 2
                          }, 1024))
                    ]),
                    _: 2
                  }, 1024),
                  (index < breadcrumbs.value.length - 1)
                    ? (_openBlock(), _createBlock(_unref(BreadcrumbSeparator), { key: 0 }))
                    : _createCommentVNode("", true)
                ], 64))
              }), 128))
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(GlobalSearch),
        (_unref(canSwitchTenant) && selectableTenants.value.length)
          ? (_openBlock(), _createBlock(_unref(DropdownMenu), { key: 0 }, {
              default: _withCtx(() => [
                _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Button), {
                      variant: "outline",
                      size: "sm",
                      class: "gap-2"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Building2), { class: "h-4 w-4" }),
                        _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(currentTenant)?.name || 'Tenant'), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DropdownMenuContent), {
                  align: "end",
                  class: "w-56"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DropdownMenuLabel), null, {
                      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                        _createTextVNode("Cambiar de tenant", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_unref(DropdownMenuSeparator)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectableTenants.value, (tenant) => {
                      return (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                        key: tenant.id,
                        class: "flex items-center justify-between gap-2",
                        onClick: ($event: any) => (handleSelectTenant(tenant.id))
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_7, _toDisplayString(tenant.name), 1),
                          (tenant.id === _unref(effectiveTenantId))
                            ? (_openBlock(), _createBlock(_unref(Check), {
                                key: 0,
                                class: "h-4 w-4 shrink-0"
                              }))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1032, ["onClick"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_unref(Button), {
          variant: "ghost",
          size: "icon",
          onClick: toggleLanguage
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Globe), { class: "h-5 w-5" }),
            _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(t)('settings.language.title')), 1)
          ]),
          _: 1
        }),
        _createVNode(ThemeToggle),
        _createVNode(_unref(Button), {
          variant: "ghost",
          size: "icon",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(tasksWidget).toggle()))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(CheckSquare), { class: "h-5 w-5" })
          ]),
          _: 1
        }),
        _createVNode(_unref(Button), {
          variant: "ghost",
          size: "icon",
          onClick: openMeetings
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(CalendarDays), { class: "h-5 w-5" })
          ]),
          _: 1
        }),
        _createVNode(MeetingsWidget, {
          open: isMeetingsOpen.value,
          "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => ((isMeetingsOpen).value = $event))
        }, null, 8, ["open"]),
        _createVNode(_unref(Button), {
          variant: "ghost",
          size: "icon",
          class: "relative",
          onClick: openNotifications
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Bell), { class: "h-5 w-5" }),
            (_unref(notificationsStore).hasUnread)
              ? (_openBlock(), _createBlock(_unref(Badge), {
                  key: 0,
                  variant: "destructive",
                  class: "absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(notificationsStore).unreadCount > 9 ? '9+' : _unref(notificationsStore).unreadCount), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)('navigation.notifications')), 1)
          ]),
          _: 1
        }),
        _createVNode(NotificationsDrawer, {
          open: isNotificationsOpen.value,
          "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => ((isNotificationsOpen).value = $event))
        }, null, 8, ["open"]),
        _createVNode(_unref(DropdownMenu), null, {
          default: _withCtx(() => [
            _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
              default: _withCtx(() => [
                _createVNode(_unref(Button), {
                  variant: "ghost",
                  class: "relative h-9 w-9 rounded-full"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Avatar), { class: "h-9 w-9" }, {
                      default: _withCtx(() => [
                        (_unref(user)?.avatarUrl)
                          ? (_openBlock(), _createBlock(_unref(AvatarImage), {
                              key: 0,
                              src: _unref(user).avatarUrl,
                              alt: _unref(user).displayName
                            }, null, 8, ["src", "alt"]))
                          : _createCommentVNode("", true),
                        _createVNode(_unref(AvatarFallback), null, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(getInitials)(_unref(user)?.displayName)), 1)
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(DropdownMenuContent), {
              class: "w-56",
              align: "end"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DropdownMenuLabel), { class: "font-normal" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(user)?.displayName), 1),
                      _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(user)?.email), 1),
                      (_unref(currentTenant)?.name)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(_unref(currentTenant).name), 1))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DropdownMenuSeparator)),
                _createVNode(_unref(DropdownMenuItem), { onClick: goToProfile }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(User), { class: "mr-2 h-4 w-4" }),
                    _createTextVNode(" " + _toDisplayString(_unref(t)('navigation.profile')), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DropdownMenuItem), { onClick: goToSettings }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Settings), { class: "mr-2 h-4 w-4" }),
                    _createTextVNode(" " + _toDisplayString(_unref(t)('navigation.settings')), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DropdownMenuSeparator)),
                _createVNode(_unref(DropdownMenuItem), {
                  onClick: handleLogout,
                  class: "text-destructive"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(LogOut), { class: "mr-2 h-4 w-4" }),
                    _createTextVNode(" " + _toDisplayString(_unref(t)('auth.logout')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ])
    ])
  ], 64))
}
}

})